<template>
  <div class="order-history">
    <v-card :class="`card-container ${darkMode ? 'dark-border' : 'light-border'}`" flat>
      <h2>{{ $t('store.orderHistory') }}</h2>
      <div class="progress" v-if="loadingHistory">
        <pulse-loader :color="$options.loaderColor"></pulse-loader>
      </div>

      <transition-group name="list" appear v-else-if="!loadingHistory && orderHistoryLength > 0">
        <v-list subheader three-line v-for="(order, index) in orderHistory" :key="order.cartID">
          <v-subheader class="text-h6 text-truncate">
            <v-list-item-content>
              <router-link
                :to="{
                  props: true,
                  name: 'OrderReceipt',
                  params: {
                    id: order.transactionID,
                  },
                }"
              >
                <v-list-item-title
                  :class="[
                    $vuetify.theme.dark
                      ? 'grey--text text--lighten-2'
                      : 'grey--text text--darken-1',
                  ]"
                >
                  <v-icon>info</v-icon>
                  {{ $t('store.order') }}:
                  {{
                    order.orderedAt
                      ? $d(new Date(order.orderedAt), 'base')
                      : $d(new Date(order.createdAt), 'base')
                  }}
                  &#124;
                  {{ order.transactionID }}
                </v-list-item-title>
              </router-link>
            </v-list-item-content>
            <v-list-item-action class="text-right text-subtitle-1">
              <router-link
                :to="{
                  props: true,
                  name: 'OrderReceipt',
                  params: {
                    id: order.transactionID,
                  },
                }"
              >
                {{ $t('store.general.details') }}
              </router-link>
            </v-list-item-action>
          </v-subheader>

          <v-list-item v-for="subItem in order.items" :key="subItem.itemID">
            <v-list-item-content class="mx-6 pl-2">
              <v-list-item-subtitle>
                <span class="font-weight-bold">{{ $t('store.product') }}:</span>
                {{ subItem.webtitle }} ({{ subItem.skucode }}) -
                {{ $n(utility_centsToDollars(subItem.price), 'currency') }}
              </v-list-item-subtitle>
              <v-list-item-subtitle
                v-if="subItem.forType === 'license' && checkForValidLicense(subItem.forLicense)"
              >
                <span class="font-weight-bold">{{ $t('store.general.license') }}:</span>
                {{ subItem.forLicense }}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-if="subItem.forType === 'productcode'">
                <span class="font-weight-bold">{{ $t('store.shoppingCart.orderedKeys') }}:</span>
                {{ subItem.forLicenseType | utility_capitalize }} ({{ subItem.quantity }})
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content class="mx-6 pl-2">
              <v-list-item-subtitle>
                <span class="font-weight-bold">
                  {{ $t('store.order') }} {{ $t('store.shoppingCart.total') }}:
                </span>
                <span class="success--text font-weight-bold" v-if="order.delayStart">
                  {{ $n(utility_centsToDollars(0), 'currency') }}
                </span>
                <span v-else>
                  {{ $n(utility_centsToDollars(order.total), 'currency') }}
                </span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-divider
            class="mt-4"
            v-if="orderHistoryLength > 0 && index !== orderHistoryLength - 1"
          ></v-divider>
        </v-list>
      </transition-group>

      <v-alert
        v-else-if="!loadingHistory && orderHistoryLength === 0"
        :value="true"
        class="mt-4"
        style="border-radius: 0"
        color="error"
        icon="warning"
        outlined
      >
        {{ $t('store.shoppingCart.noOrders') }}
      </v-alert>
    </v-card>

    <SettingsBottomNav :limitedUser="limitedUser" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import meMixin from '@/mixins/meMixin';
import utilityMixin from '@/mixins/utilityMixin';
import SettingsBottomNav from '@/components/SettingsBottomNav';
import { loaderColor } from '@/lib/constants';

export default {
  name: 'OrderHistory',
  mixins: [meMixin, utilityMixin],

  props: {
    limitedUser: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    SettingsBottomNav,
  },

  data() {
    return {
      loadingHistory: false,
    };
  },

  created() {
    //* non-reactive constants
    this.$options.loaderColor = loaderColor;
  },

  mounted() {
    //* Don't accept an id in the route. Allows anyone to view order history
    this.loadingHistory = true;
    if (this.getMe?.id) {
      this.$store
        .dispatch('shop/getOrderHistory', this.getMe?.id)
        .then(() => {
          // console.log('res:', res);
          this.loadingHistory = false;
        })
        .catch((err) => {
          console.error('err:', err);
          this.$root.vtoast.show({
            text: `Error ${err?.status ? err.status : ''}: Error loading order history`,
            color: 'error',
          });
          this.loadingHistory = false;
        });
    } else {
      this.$store
        .dispatch('users/me')
        .then((res) => {
          this.$store.dispatch('shop/getOrderHistory', res?.id).catch((err) => {
            console.error('err:', err);
          });
          this.loadingHistory = false;
        })
        .catch((err) => {
          console.error('err:', err);
          this.loadingHistory = false;
        });
    }
  },

  beforeDestroy() {
    this.$store.dispatch('shop/resetOrderHistory');
    this.loadingHistory = false;
  },

  computed: {
    ...mapGetters({
      loading: 'shop/loading',
      orderHistory: 'shop/orderHistory',
    }),

    orderHistoryLength() {
      if (this.orderHistory?.length) {
        return this.orderHistory?.length;
      }
      return 0;
    },
  },

  methods: {
    checkForValidLicense(license) {
      return !license.includes('00000000-0000-0000-0000-000000000000');
    },
  },
};
</script>

<style scoped>
.order-history {
  margin-top: -65px;
  min-height: calc(60vh - 65px) !important;
}

.order-history .card-container {
  padding: 30px !important;
  border-radius: 10px;
  border-radius: var(--border-10);
}

.progress {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

* >>> .v-list--three-line .v-list-item {
  height: auto;
  min-height: 40px;
}

@media (max-width: 600px) {
  .order-history {
    margin-top: 0px;
  }
}
</style>
